import ImagePlacehodler from "@components/ArticleCard/components/ImagePlacehodler";
import { getFormattedCaaSImageUrl } from "@components/OptimisedImage/helper";
import { faCircle, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CardImage } from "@particles/card/utils/types";
import { CaaSImageFilters } from "@typings/OptimisedImage";
import { IS_PLAYWRIGHT } from "@util/constant";
import { cn } from "@util/helpers";

type CardImageV2Props = {
  /** This is the classname of the component. */
  className?: string;
  /** Image class name */
  imageClassName?: string;
  /** Aspect ratio of the image */
  aspectRatio?: string;
  /** Flag to indicate if it is a video */
  isVideo?: boolean;
} & CardImage;

/**
 * CardImage V2 component that renders an image or video thumbnail with optional filters and styles.
 */
const CardImageV2 = ({
  className,
  src,
  alt,
  width,
  height,
  filters,
  withBlurBackground = false,
  imageClassName,
  aspectRatio = "aspect-3x2",
  isVideo = false,
}: CardImageV2Props): React.ReactElement => {
  if (IS_PLAYWRIGHT) {
    return <ImagePlacehodler aspectRatio={aspectRatio} className={className} />;
  }

  const defaultFilters: CaaSImageFilters = {
    w: 1570,
    h: 1046,
    dpr: 1,
    f: "webp",
  };
  const imgSrc = getFormattedCaaSImageUrl(src, filters || defaultFilters);

  return (
    <div
      data-testid="card-image-v2-component"
      className={cn("relative", className)}
    >
      {withBlurBackground ? (
        <div
          style={{
            background: `center / cover no-repeat url(${imgSrc})`,
          }}
          className="absolute top-0 z-[-1] h-full w-full overflow-hidden blur-[100px]"
        />
      ) : null}

      <img
        className={cn(
          "relative z-10 h-full w-full object-contain",
          imageClassName,
          aspectRatio
        )}
        src={imgSrc}
        alt={alt}
        width={width}
        height={height}
      />

      {isVideo ? (
        <div className="h-full w-full">
          <FontAwesomeIcon
            icon={faCircle}
            inverse
            className="fa-stack-2x z-10 opacity-80"
          />

          <FontAwesomeIcon icon={faPlay} className="fa-stack-1x z-10" />
        </div>
      ) : null}
    </div>
  );
};

export default CardImageV2;
